// projectedMeters.js
// Stand-alone helper that decorates an array of orders
// with a `projectedMeters` field.  Works in plain JS & React projects.

import axiosInstance from "../../../axios/axiosConfig"

// ────────────────────────────────────────────────────────────────
// default cache – keeps one Knife response per code for the
// lifetime of the app (or until you call clearKnifeCache()).
// ────────────────────────────────────────────────────────────────
const knifeCache = new Map()

/**
 * Add `projectedMeters` to every order object.
 *
 * @param {Array<Object>} orders              Array of raw order docs
 * @param {Object} [opts]
 * @param {Map}    [opts.cache=knifeCache]    Cache object (Map-like)
 * @param {Object} [opts.axios=axiosInstance] Axios instance to perform requests
 * @returns {Promise<Array<Object>>}
 */
export async function addProjectedMeters(
  orders,
  { cache = knifeCache, axios = axiosInstance } = {},
) {
  return Promise.all(
    orders.map(async (order) => {
      const knifeCode = order?.selectedProduct?.knifeCode

      // no or "none" code → 0 m
      if (!knifeCode || knifeCode === "none") {
        return { ...order, projectedMeters: 0 }
      }

      try {
        let knife = cache.get(knifeCode)

        if (!knife) {
          const { data } = await axios.get(
            `/api/searchByCode?code=${encodeURIComponent(knifeCode)}`,
          )
          knife = data
          cache.set(knifeCode, knife)
        }
        const correctColumns = order.selectedProduct.numberOfColumns

        const countInThousands = order.labelCount / 1000
        const a1 = (knife.height + knife.gapRaport) * 0.001
        const a2 = Number((1000 / correctColumns).toFixed(2))
        const a3 = Math.ceil(a1 * a2)
        const projectedMeters = Math.round(a3 * countInThousands)

        return { ...order, projectedMeters }
      } catch (err) {
        console.error("Failed to fetch knife data:", err)
        return { ...order, projectedMeters: 0 }
      }
    }),
  )
}

/** Manually wipe the knife cache (e.g. when user logs out). */
export function clearKnifeCache() {
  knifeCache.clear()
}
